import {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {createArticle, editArticle} from "../api/Article";
import {useArticleByID} from "../../hooks/use-articles";
import {API_URL, DEBUG_MODE} from "../../api/RequestConstants";

import Input from "../../сomponents/UI/form/Input";
import Editor from "../components/form/Editor";
import useInput from "../../hooks/use-input";
import EditPageLayout from "../layouts/EditPageLayout";

const ArticleEditPage = () => {
    const navigate = useNavigate();
    const params = useParams();

    const [banner, setBanner] = useState(null);
    const [previewBanner, setPreviewBanner] = useState(null);
    const [showProcessLoading, setShowProcessLoading] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [articleLoaded, setArticleLoaded] = useState(false);

    const quillRef = useRef();

    const article = useArticleByID(params.id);

    const {
        value: title,
        setValue: setTitle,
        isValid: enteredTitleIsValid,
        hasError: titleInputHasError,
        valueChangeHandler: titleChangedHandler,
        inputBlurHandler: titleBlurHandler
    } = useInput(value => value.trim() !== '');

    const {
        value: authorName,
        setValue: setAuthorName,
        isValid: enteredAuthorNameIsValid,
        hasError: authorNameInputHasError,
        valueChangeHandler: authorNameChangedHandler,
        inputBlurHandler: authorNameBlurHandler
    } = useInput(value => value.trim() !== '');

    const {
        value: hashtags,
        setValue: setHashtags,
        isValid: enteredHashtagsIsValid,
        hasError: hashtagsInputHasError,
        valueChangeHandler: hashtagsChangedHandler,
        inputBlurHandler: hashtagsBlurHandler
    } = useInput(value => value.trim() !== '');

    const {
        value: metaTitle,
        setValue: setMetaTitle,
        valueChangeHandler: metaTitleChangedHandler
    } = useInput(() => true);

    const {
        value: metaAlt,
        setValue: setMetaAlt,
        valueChangeHandler: metaAltChangedHandler
    } = useInput(() => true);

    useEffect(() => {
        if (article != null && !articleLoaded) {
            setTitle(article.title);
            setAuthorName(article.author_name);
            setHashtags(article.hashtags);
            setPreviewBanner(API_URL + '/' + article.banner.path);
            setMetaTitle(article.banner.meta_title);
            setMetaAlt(article.banner.meta_alt);
            setArticleLoaded(true);

            const quill = quillRef.current;
            const delta = quill.clipboard.convert({html: article.content});
            quill.setContents(delta, 'silent');
        }
    }, [article, setTitle, setAuthorName, setHashtags, setMetaAlt, setMetaTitle, setArticleLoaded, articleLoaded]);

    const formIsValid = enteredTitleIsValid && enteredHashtagsIsValid && enteredAuthorNameIsValid;

    const onSubmitHandler = useCallback(async (e) => {
        e.preventDefault();
        if (formIsValid) {
            const editMode = params.id;
            setShowProcessLoading(true);
            setIsDone(false);
            setErrorMessage("");
            let response = {success: false, message: ''};
            try {
                if (editMode) {
                    response = await editArticle(params.id, title, authorName, hashtags, quillRef.current.getSemanticHTML(0), banner, metaTitle, metaAlt);
                } else {
                    response = await createArticle(title, authorName, hashtags, quillRef.current.getSemanticHTML(0), banner, metaTitle, metaAlt);
                }

                if (response.success) {
                    setIsDone(true);
                    navigate('/crm/articles');
                }
                else {
                    setShowProcessLoading(false);
                    setErrorMessage(response.message);

                    if (DEBUG_MODE) {
                        console.log(response);
                    }
                }
            } catch (err) {
                setErrorMessage(err.message);
                setShowProcessLoading(false);
            }
        } else {
            setErrorMessage("Некоторые поля не прошли валидацию!");
        }
    }, [formIsValid, title, authorName, hashtags, params.id, navigate, banner, metaTitle, metaAlt]);

    const onImageSelected = useCallback((event) => {
        let image = event.target.files[0];
        if (image === undefined) image = null;

        setBanner(image);
        if (image != null) {
            setPreviewBanner(URL.createObjectURL(image));
        } else {
            setPreviewBanner(null);
        }
    }, [setBanner, setPreviewBanner]);

    let pageTitle = 'Создание статьи';
    let actionName = 'Создать статью';
    if (params.id) {
        pageTitle = 'Редактирование статьи';
        actionName = 'Изменить статью';
    }

    return (
        <EditPageLayout
            pageTitle={pageTitle}
            actionName={actionName}
            errorMessage={errorMessage}
            showProcessLoading={showProcessLoading}
            isDone={isDone}
            onSubmit={onSubmitHandler}
        >
            <div className='image-form'>
                <div>
                    <Input
                        type='file'
                        label={'Баннер'}
                        name='article_banner'
                        onChange={onImageSelected}
                        accept=".jpeg,.png,.jpg,.webp"
                    />
                    {previewBanner && <img src={previewBanner} alt={'banner preview'} />}
                </div>
                <div>
                    <Input
                        type='text'
                        label={'Meta Title'}
                        name='meta_title'
                        placeholder='заголовок картинки'
                        maxLength={255}
                        value={metaTitle}
                        onChange={metaTitleChangedHandler}
                    />
                    <Input
                        type='text'
                        label={'Meta Alt'}
                        name='meta_alt'
                        placeholder='альтернативный текст картинки'
                        maxLength={255}
                        value={metaAlt}
                        onChange={metaAltChangedHandler}
                    />
                </div>
            </div>

            <Input
                type='text'
                label={'Название статьи'}
                name='article_title'
                placeholder='Как купить слона?'
                maxLength={255}
                value={title}
                isError={titleInputHasError}
                onChange={titleChangedHandler}
                onBlur={titleBlurHandler}
            />

            <Input
                type='text'
                label={'Автор'}
                name='article_author_name'
                placeholder='Marat Degtear'
                value={authorName}
                maxLength={255}
                isError={authorNameInputHasError}
                onChange={authorNameChangedHandler}
                onBlur={authorNameBlurHandler}
            />

            <Input
                type='text'
                label={'Хештеги'}
                name='article_hashtags'
                placeholder='#Productdesign #Business #Marketing'
                value={hashtags}
                maxLength={512}
                isError={hashtagsInputHasError}
                onChange={hashtagsChangedHandler}
                onBlur={hashtagsBlurHandler}
            />

            <Editor
                ref={quillRef}
            />
        </EditPageLayout>
    );
};

export default ArticleEditPage;